import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.classList.add("mobile-menu-shown");
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.classList.remove("mobile-menu-shown");
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.classList.remove("mobile-menu-shown");
    };
  }, [mobileMenuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        mobileMenuOpen &&
        !target.closest(".responsive-menu-wrapper") &&
        !target.closest(".responsive-menu-toggle")
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Sticky header functionality
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const header = document.querySelector("header");
      const headerParent = header?.parentElement;

      if (header && headerParent) {
        const height = header.offsetHeight;

        if (scrollTop > 0) {
          header.classList.add("minimized");
          headerParent.style.paddingTop = `${height}px`;
        } else {
          header.classList.remove("minimized");
          headerParent.style.paddingTop = "0";
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    window.addEventListener("orientationchange", handleScroll);

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      window.removeEventListener("orientationchange", handleScroll);
    };
  }, []);

  return (
    <header role="banner">
      <div className="branding">
        <div className="wrapper">
          <div className="region region-header">
            <div
              id="block-hkustheaderblockaffiliate"
              className="block block-hkust-signature-affiliate block-header-block"
            >
              <div className="wrapper">
                <div className="block-body">
                  {/* Desktop Header */}
                  <div className="header-data-pc fade-bg">
                    <div id="header-desktop">
                      <div className="site-header-content clearfix">
                        <div className="right"></div>
                      </div>
                    </div>

                    <div id="main-menu">
                      <div className="main-menu clearfix">
                        <div className="site-profile">
                          <div className="site-info style-0">
                            <div className="combined-logo">
                              <Link to="/">
                                <img
                                  src="/images/hkust/AI literacy hub logo_full_transparent.png"
                                  alt="HKUST AI Literacy Hub"
                                  style={{
                                    height: "45px",
                                    verticalAlign: "middle",
                                  }}
                                />
                              </Link>
                            </div>
                          </div>
                        </div>
                        <div className="menu">
                          <ul className="menu-lists">
                            <li className="parent-item">
                              <Link to="/">Home</Link>
                            </li>
                            <li className="parent-item">
                              <Link to="/about">About</Link>
                            </li>
                            <li className="parent-item">
                              <Link to="/blog">Blog</Link>
                            </li>
                            <li className="parent-item">
                              <Link to="/newsletter">Newsletter</Link>
                            </li>
                            <li className="parent-item">
                              <Link to="/initiatives">Initiatives</Link>
                            </li>
                            <li className="parent-item">
                              <Link to="/design-thinking">Design Thinking</Link>
                            </li>
                            <li className="parent-item">
                              <Link to="/research">Research</Link>
                            </li>
                            <li className="parent-item">
                              <Link to="/#contact-us">Contact Us</Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Header */}
                  <div className="header-data-mobile fade-bg clearfix">
                    <div className="content clearfix">
                      <div className="site-profile-wrapper">
                        <div className="site-profile-content">
                          <div id="header-mobile">
                            <div className="site-header-content clearfix">
                              <div className="search"></div>
                            </div>
                          </div>
                          <div className="site-profile style-0">
                            <div className="site-info">
                              <div className="combined-logo-mobile">
                                <Link to="/">
                                  <img
                                    src="/images/hkust/AI literacy hub logo_full_transparent.png"
                                    alt="HKUST AI Literacy Hub"
                                    style={{
                                      height: "80px",
                                      verticalAlign: "middle",
                                    }}
                                  />
                                </Link>
                              </div>
                            </div>
                            <div className="responsive-menu-toggle">
                              <button
                                type="button"
                                className="menus-bar"
                                onClick={(e) => {
                                  e.preventDefault();
                                  toggleMobileMenu();
                                }}
                                aria-label="Toggle menu"
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  cursor: "pointer",
                                }}
                              >
                                <span
                                  className="fa fa-bars"
                                  aria-hidden="true"
                                ></span>
                                <span className="visually-hidden">Menu</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`responsive-menu-wrapper ${
                          mobileMenuOpen ? "active" : ""
                        }`}
                        onClick={(e) => {
                          // Close menu when clicking on overlay (not the menu itself)
                          if (e.target === e.currentTarget) {
                            setMobileMenuOpen(false);
                          }
                        }}
                      >
                        <div
                          className="responsive-menu"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="scrollable-wrapper mCustomScrollbar">
                            <div
                              style={{
                                padding: "1rem 1.5rem",
                                borderBottom: "1px solid #eee",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flex: "0 0 auto",
                              }}
                            >
                              <strong
                                style={{ fontSize: "1.2rem", color: "#333" }}
                              >
                                Menu
                              </strong>
                              <button
                                onClick={() => setMobileMenuOpen(false)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  fontSize: "1.5rem",
                                  color: "#666",
                                  cursor: "pointer",
                                  padding: "0.25rem 0.5rem",
                                  lineHeight: 1,
                                }}
                                aria-label="Close menu"
                              >
                                ×
                              </button>
                            </div>
                            <ul className="menu-lists">
                              <li>
                                <Link
                                  className="parent-item"
                                  to="/"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  Home
                                </Link>
                              </li>
                              <li>
                                <Link
                                  className="parent-item"
                                  to="/about"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  About
                                </Link>
                              </li>
                              <li>
                                <Link
                                  className="parent-item"
                                  to="/blog"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  Blog
                                </Link>
                              </li>
                              <li>
                                <Link
                                  className="parent-item"
                                  to="/newsletter"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  Newsletter
                                </Link>
                              </li>
                              <li>
                                <Link
                                  className="parent-item"
                                  to="/initiatives"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  Initiatives
                                </Link>
                              </li>
                              <li>
                                <Link
                                  className="parent-item"
                                  to="/design-thinking"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  Design Thinking
                                </Link>
                              </li>
                              <li>
                                <Link
                                  className="parent-item"
                                  to="/research"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  Research
                                </Link>
                              </li>
                              <li>
                                <Link
                                  className="parent-item"
                                  to="/#contact-us"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  Contact Us
                                </Link>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="region-split region-header page-container">
        <div className="col"></div>
        <div className="col"></div>
      </div>
    </header>
  );
};

export default Header;
